/**
 * The facts behind /bass-lessons/zoom-setup. The page renders this, and the
 * build-time PDF guide (Plan 2) renders it too, so a price or a step changes
 * in exactly one place. Prose that belongs to one surface stays in that
 * surface's template; only facts live here.
 */

export type LessonJobId =
  'bass-in' | 'playback-in' | 'voice-in' | 'teacher-back' | 'no-loopback';

export interface LessonJob {
  readonly id: LessonJobId;
  readonly label: string;
  readonly summary: string;
}

/** The five things every lesson setup has to do, in the order the page teaches them. */
export const LESSON_JOBS: readonly LessonJob[] = [
  { id: 'bass-in', label: 'Bass → Russ', summary: 'Your bass reaches Russ.' },
  {
    id: 'playback-in',
    label: 'Playback → Russ and student',
    summary:
      'A backing track, iReal Pro, Spotify, or a metronome reaches Russ and the student.',
  },
  {
    id: 'voice-in',
    label: 'Voice → Russ',
    summary: 'Your voice reaches Russ.',
  },
  {
    id: 'teacher-back',
    label: 'Russ → student',
    summary: "Russ's voice and bass reach the student's ears.",
  },
  {
    id: 'no-loopback',
    label: 'No loopback',
    summary:
      'What Russ sends never comes back to Russ, and what the student sends never comes back to the student.',
  },
];

export const LESSON_RULE =
  'What comes out of the call must not go back into it. Every setup I recommend is a way of keeping that rule.';

/** Names the two ends of the call once, before the jobs use them. */
export const CALL_ENDS =
  'A Zoom lesson has two ends: you, the student, and me, Russ. Every job below is about sound getting from one end to the other, and never back to where it started.';

/** Russ's own rig, a mixer setup; on the page under the recommended setup (the printed guide does not carry it). */
export const WHAT_I_USE =
  "What I use myself is the same kind of setup: my amp's DI out into CH1, a separate mic into CH2, and playback from my MacBook through a small headphone preamp into CH3/4. CH1 and CH2 are the same kind of input, so bass and mic can take either one; the steps below put the mic on CH1 and the bass on CH2.";

export const IMPROVEMENTS_HEADING = 'Options that improve the lesson';

export const IMPROVEMENTS_INTRO =
  'The recommended setup is enough for a good lesson. Each option here makes one part of it better: your voice, your bass tone, how loud your headphones can go, or playing tracks from the computer. Each one names the job it changes.';

export function jobLabel(id: LessonJobId): string {
  const job = LESSON_JOBS.find((j) => j.id === id);
  if (job === undefined) {
    throw new Error(`unknown lesson job: ${id}`);
  }
  return job.label;
}

export interface ShoppingItem {
  readonly name: string;
  readonly href?: string;
  readonly price: string;
  readonly notes: string;
}

/** Transcribed from the current recommended table; keep the same four rows. */
export const RECOMMENDED_SHOPPING_LIST: readonly ShoppingItem[] = [
  {
    name: 'Rockville RockMix 4 mixer',
    href: 'https://www.amazon.com/Rockville-RockMix-Instrument-Recording-Interface/dp/B08KWPLLJ1',
    price: '~$60',
    notes: 'Comes with USB cable and power adapter.',
  },
  {
    name: 'HyperX Cloud Stinger Core headset',
    href: 'https://www.amazon.com/s?k=HyperX+Cloud+Stinger+Core+headset',
    price: '~$40',
    notes:
      'Headset with built-in mic. Includes Y-splitter cable (separates mic and headphone plugs).',
  },
  {
    name: '1/4-inch instrument cable',
    href: 'https://www.amazon.com/s?k=1%2F4+inch+instrument+cable+10+foot',
    price: '~$15',
    notes: 'Connects your bass to mixer (the larger plug).',
  },
  {
    name: '3.5mm to dual 1/4-inch cable',
    href: 'https://www.amazon.com/s?k=3.5mm+to+dual+1%2F4+inch+stereo+breakout+cable',
    price: '~$10',
    notes:
      'Connects phone/tablet to mixer for playback audio (backing tracks, metronome).',
  },
];

/** "~$125": the whole-dollar sum of the list's prices, so the page and the guide never carry a stale total. */
export function shoppingTotal(items: readonly ShoppingItem[]): string {
  const sum = items.reduce((acc, item) => {
    const dollars = /\d+/.exec(item.price)?.[0];
    if (dollars === undefined) {
      throw new Error(
        `shopping item "${item.name}" has no numeric price: ${item.price}`,
      );
    }
    return acc + Number(dollars);
  }, 0);
  return `~$${sum}`;
}

export interface SetupStep {
  readonly id: string;
  readonly title: string;
  readonly jobs: readonly LessonJobId[];
}

/** The six connection steps, with the job each one does. Ids are page anchors. */
export const SETUP_STEPS: readonly SetupStep[] = [
  {
    id: 'step-mic',
    title: 'Connect your microphone (separate-mic setup only)',
    jobs: ['voice-in'],
  },
  { id: 'step-bass', title: 'Connect your bass', jobs: ['bass-in'] },
  {
    id: 'step-playback',
    title: 'Connect your playback device',
    jobs: ['playback-in'],
  },
  {
    id: 'step-headphones',
    title: 'Connect your headphones',
    jobs: ['teacher-back', 'voice-in'],
  },
  {
    id: 'step-record-source',
    title: 'Set the Record Source switch',
    jobs: ['no-loopback'],
  },
  {
    id: 'step-mixer',
    title: 'Connect the mixer to your computer',
    jobs: ['bass-in', 'playback-in', 'voice-in', 'teacher-back'],
  },
];

export interface FiveJobsRow {
  readonly job: LessonJobId;
  readonly how: string;
}

export interface TroubleshootItem {
  readonly symptom: string;
  readonly check: string;
  readonly fix: string;
}

export interface SetupProfile {
  readonly id: string;
  readonly name: string;
  readonly rows: readonly FiveJobsRow[];
  readonly troubleshooting: readonly TroubleshootItem[];
}

export const RECOMMENDED_SETUP: SetupProfile = {
  id: 'recommended',
  name: 'Recommended setup: small mixer and a headset',
  rows: [
    { job: 'bass-in', how: 'Instrument cable into CH2 on the mixer.' },
    {
      job: 'playback-in',
      how: 'Phone or tablet into CH3/4 with the 3.5 mm to dual 1/4-inch cable.',
    },
    {
      job: 'voice-in',
      how: 'The headset mic, through the Y-splitter into the mixer.',
    },
    {
      job: 'teacher-back',
      how: 'The headset earcups from the mixer headphone jack. Zoom comes in on the computer channel.',
    },
    {
      job: 'no-loopback',
      how: 'Record Source set to INPUT MIX. Your channels go to Zoom; Zoom does not go back to Zoom.',
    },
  ],
  troubleshooting: [
    {
      symptom: 'You hear your own voice back with a delay.',
      check: 'The Record Source switch. It is on LOOPBACK.',
      fix: 'Set it to INPUT MIX.',
    },
    {
      symptom: 'I hear myself echo.',
      check: 'Same switch, same cause.',
      fix: 'Set Record Source to INPUT MIX, then have me talk again.',
    },
    {
      symptom: 'I hear your bass but not your voice, or the other way round.',
      check:
        'The channel gain for the quiet one. Watch the level light while you play or talk.',
      fix: 'Turn that channel up until the light flickers on the loud notes and stays off the rest of the time.',
    },
    {
      symptom: 'Your backing track is only in your ears and I cannot hear it.',
      check: 'The playback device is plugged into the computer, not the mixer.',
      fix: 'Move it to CH3/4 on the mixer. If the computer is the playback device, run its headphone jack into CH3/4 with the 3.5 mm cable, the same as a phone, and leave the system output on the built-in speakers or headphone jack.',
    },
    {
      symptom: 'Everything is very quiet in your headphones.',
      check: 'The mixer headphone knob and the computer channel knob.',
      fix: "Turn both up. If it is still quiet, put a small headphone amp between the mixer's headphone jack and your headphones; the live guide shows the cable under Options that improve the lesson.",
    },
  ],
};

export const OTHER_SETUPS: readonly SetupProfile[] = [
  {
    id: 'mixer-no-switch',
    name: 'A USB mixer without a Record Source switch',
    rows: [
      { job: 'bass-in', how: 'Instrument cable into a line or Hi-Z channel.' },
      { job: 'playback-in', how: 'Phone or tablet into a stereo channel.' },
      {
        job: 'voice-in',
        how: 'Mic into a mic channel, or the headset through a Y-splitter.',
      },
      { job: 'teacher-back', how: 'Headphones on the mixer headphone jack.' },
      {
        job: 'no-loopback',
        how: 'Nothing on the mixer does this job, so you do it: stay muted in Zoom when you are not playing or talking. It works, but I lose the ability to hear you all the time.',
      },
    ],
    troubleshooting: [
      {
        symptom: 'I hear myself echo as soon as you unmute.',
        check:
          'This mixer sends everything it receives back over USB, including my audio from Zoom.',
        fix: 'Mute in Zoom between playing, or move to the recommended mixer or the interface setup below.',
      },
    ],
  },
  {
    id: 'interface-virtual-devices',
    name: 'An audio interface and virtual audio devices',
    rows: [
      {
        job: 'bass-in',
        how: 'Bass into an interface input, straight or through a DI.',
      },
      {
        job: 'playback-in',
        how: 'A virtual device (Loopback on the Mac) collects the app you play from. Zoom picks it up as part of the send.',
      },
      { job: 'voice-in', how: 'Mic into the other interface input.' },
      {
        job: 'teacher-back',
        how: 'Zoom is told to play to your playback device, which feeds your headphones on the interface.',
      },
      {
        job: 'no-loopback',
        how: 'Zoom is told to send the virtual device that holds bass, mic and playback, and nothing else. What Zoom plays is not in that device, so it never goes back.',
      },
    ],
    troubleshooting: [
      {
        symptom: 'I hear your backing track twice, or I hear myself.',
        check:
          'Which device Zoom is sending. It is the one that also contains Zoom output.',
        fix: 'Point Zoom at the send-only virtual device. Keep Zoom playback out of it.',
      },
      {
        symptom: 'I hear nothing from you.',
        check: 'The Zoom microphone setting is still on the built-in mic.',
        fix: 'Pick the virtual device as the microphone in Zoom audio settings.',
      },
    ],
  },
];

export interface Adaptation {
  readonly id: string;
  readonly job: LessonJobId;
  readonly title: string;
  readonly what: string;
  readonly extra: string;
  readonly troubleshooting: readonly TroubleshootItem[];
}

/** Per-job substitutions for the pieces that can be swapped without changing the setup. */
export const ADAPTATIONS: readonly Adaptation[] = [
  {
    id: 'separate-mic-and-headphones',
    job: 'voice-in',
    title: 'A separate microphone and headphones instead of a headset',
    what: 'The mic plugs into CH1 with an XLR cable, the headphones into the headphone jack. Every other step stays the same.',
    extra:
      'One XLR cable, a mic stand, and a 1/4-inch adapter if your headphones have the small plug. About $140 more than the headset.',
    troubleshooting: [
      {
        symptom: 'The mic is very quiet even with the gain up.',
        check:
          'It is a dynamic mic with a low output, or a condenser without phantom power.',
        fix: 'For a condenser, turn on +48V. For a quiet dynamic, add an inline mic booster (next item).',
      },
    ],
  },
  {
    id: 'inline-mic-booster',
    job: 'voice-in',
    title: 'An inline mic booster for a quiet dynamic mic',
    what: 'A small box between the mic and CH1 that adds clean gain. It needs the mixer +48V switch on to power itself.',
    extra: 'One extra XLR cable.',
    troubleshooting: [
      {
        symptom: 'Still quiet.',
        check: 'The +48V switch is off, so the booster is not powered.',
        fix: 'Turn +48V on.',
      },
    ],
  },
  {
    id: 'di-or-preamp-pedal',
    job: 'bass-in',
    title: 'A DI box or a preamp pedal with a DI out',
    what: 'Bass into the box, the XLR out of the box into CH1 or CH2. You get your own tone shaping before the mixer, and a balanced signal that picks up less noise.',
    extra: 'One XLR cable.',
    troubleshooting: [
      {
        symptom: 'Hum or buzz.',
        check: 'The ground lift switch on the box.',
        fix: 'Flip it.',
      },
    ],
  },
  {
    id: 'headphone-amp',
    job: 'teacher-back',
    title: 'A headphone amp when the mixer is too quiet',
    what: 'A small amp between the mixer headphone jack and your headphones. Some studio headphones need more than a small mixer gives.',
    extra: 'One short 1/4-inch cable.',
    troubleshooting: [
      {
        symptom: 'Loud enough now, but distorted.',
        check: 'The mixer headphone knob is all the way up.',
        fix: 'Turn the mixer down and the amp up.',
      },
    ],
  },
  {
    id: 'phone-or-computer-playback',
    job: 'playback-in',
    title: 'Playing backing tracks from the computer instead of a phone',
    what: 'It works if the computer keeps its system output on the built-in speakers or the headphone jack and only Zoom uses the mixer. Run the headphone jack into CH3/4 with the 3.5 mm cable, the same as a phone. A phone or tablet is simpler because nothing else on it is talking to the mixer.',
    extra:
      'Nothing, if the computer has a headphone jack. A USB-C to 3.5 mm adapter if it does not.',
    troubleshooting: [
      {
        symptom: 'I cannot hear the track, or I hear it twice.',
        check: 'Which output the track app is using.',
        fix: 'Send the app to the mixer and keep Zoom on the mixer too. If it fights you, use a phone.',
      },
    ],
  },
];

/** Problems that are not about any one setup. */
export const GENERAL_TROUBLESHOOTING: readonly TroubleshootItem[] = [
  {
    symptom: 'Sound drops out or stutters, or the video freezes.',
    check:
      'The internet first: are you on wifi, and is anyone else streaming? Run a speed test.',
    fix: 'Plug into the router with an ethernet cable if you can. Otherwise move closer to it and pause other downloads.',
  },
  {
    symptom: 'Zoom lags even though the internet tests fine.',
    check:
      'Memory and CPU. Open Activity Monitor on a Mac or Task Manager on Windows. A laptop with 8 GB and a browser full of tabs runs out of room.',
    fix: 'Quit everything except Zoom and the app you play tracks from. Restart the computer before the lesson if it has been up for days.',
  },
  {
    symptom: 'No sound at all, either way.',
    check:
      'Zoom audio settings. The microphone and the speaker are set to the built-in ones, not the mixer.',
    fix: 'Pick the USB mixer for both. Test with the Zoom microphone test before the lesson starts.',
  },
];

/** Quoted word for word by the home page and the FAQ; the hub-body spec asserts it. */
export const HOME_GEAR_PHRASE =
  'computer with solid internet, headphones, a mic, and a small audio interface or mixer';
